import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  searchReservations,
  createReservation,
  submitAllToUbyport,
  ReservationFilter,
  ReservationPageResponse,
  Reservation
} from '../api.reservations';
import { getApartments, Apartment } from '../api.apartments';
import Pagination from '../components/Pagination';
import ReservationItem from '../components/ReservationItem';
import PlusIcon from '../components/PlusIcon';
import FilterIcon from '../components/FilterIcon';
import EditIcon from '../components/icons/EditIcon';
import useIsMobile from '../hooks/useIsMobile';
import { colors, ACTION_BTN_H, ICON_BTN_SIZE, ACTION_BTN_RADIUS } from '../theme';
import { useLang } from '../i18n/LanguageContext';
import { reservationsText } from '../i18n/pages/reservations';
import { formatStayRange } from '../utils/stayDates';
import StayHours from '../components/StayHours';
import LuggageStatusIcon from '../components/LuggageStatusIcon';
import { common } from '../i18n/pages/common';

// TypeScript type refresh forced by this comment.

const getToken = () => localStorage.getItem('token') || '';

const dateAddDays = (iso: string, days: number): string => {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const API_BASE = process.env.REACT_APP_API_URL || '/api';
const SHOW_PAST_STORAGE_KEY = 'reservations.showPast';
const PAGE_SIZE_STORAGE_KEY = 'reservations.pageSize';
const DEFAULT_PAGE_SIZE = 25;

const todayIso = () => new Date().toISOString().split('T')[0];
const RESERVATION_PAGE_SIZE_OPTIONS = [25, 50, 100];

type ReservationSortField = 'apartment' | 'checkInDate' | 'checkOutDate';
type ReservationSortDirection = 'asc' | 'desc';

const Reservations: React.FC = () => {
  const navigate = useNavigate();
  const { lang } = useLang();
  const t = reservationsText[lang];
  const tc = common[lang];
  const [data, setData] = useState<ReservationPageResponse | null>(null);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createForm, setCreateForm] = useState<Reservation>({
    apartmentId: 0,
    guestName: '',
    checkInDate: '',
    checkOutDate: '',
    numberOfGuests: 1,
    notes: '',
  });

  const [submittingUbyportId, setSubmittingUbyportId] = useState<number | null>(null);

  // Filters/sort/paging live in the URL (?apartmentId=&guestName=&page=&...) so that navigating to
  // a reservation and pressing Back restores the exact same filtered view, instead of resetting to
  // an unfiltered list. Read once on mount; kept in sync via the effect below.
  const [searchParams, setSearchParams] = useSearchParams();
  const paramStr = (key: string): string | undefined => searchParams.get(key) || undefined;
  const paramNum = (key: string): number | undefined => {
    const v = searchParams.get(key);
    if (!v) return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };
  const initialPageSize = (() => {
    const fromUrl = paramNum('size');
    if (fromUrl && RESERVATION_PAGE_SIZE_OPTIONS.includes(fromUrl)) return fromUrl;
    const stored = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
    const parsed = stored ? parseInt(stored, 10) : NaN;
    return RESERVATION_PAGE_SIZE_OPTIONS.includes(parsed) ? parsed : DEFAULT_PAGE_SIZE;
  })();
  const initialShowPast = searchParams.has('showPast')
    ? searchParams.get('showPast') === 'true'
    : localStorage.getItem(SHOW_PAST_STORAGE_KEY) === 'true';

  const [filter, setFilter] = useState<ReservationFilter>(() => ({
    page: paramNum('page') ?? 0,
    size: initialPageSize,
    sortBy: (paramStr('sortBy') as ReservationFilter['sortBy']) ?? 'checkInDate',
    sortDirection: (paramStr('sortDirection') as ReservationFilter['sortDirection']) ?? 'asc',
    apartmentId: paramNum('apartmentId'),
    checkInAfter: paramStr('checkInAfter'),
    checkOutBefore: paramStr('checkOutBefore'),
    guestName: paramStr('guestName'),
    guestPhone: paramStr('guestPhone'),
    checkOutAfter: initialShowPast ? undefined : todayIso(),
  }));
  const [filterOpen, setFilterOpen] = useState(false);
  const [pageSize, setPageSize] = useState<number>(initialPageSize);
  const [showPast, setShowPast] = useState(initialShowPast);
  const [sortField, setSortField] = useState<ReservationSortField>((filter.sortBy as ReservationSortField) ?? 'checkInDate');
  const [sortDirection, setSortDirection] = useState<ReservationSortDirection>(filter.sortDirection ?? 'asc');
  const isMobile = useIsMobile();

  // Keep the URL in sync with the current filters — replace (not push) so typing/paging doesn't
  // spam browser history; the point is that leaving and returning to this page lands back here.
  useEffect(() => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      const set = (key: string, value: string | number | undefined | null) => {
        if (value === undefined || value === null || value === '') p.delete(key);
        else p.set(key, String(value));
      };
      set('page', filter.page ? filter.page : undefined);
      set('size', filter.size && filter.size !== DEFAULT_PAGE_SIZE ? filter.size : undefined);
      set('sortBy', filter.sortBy && filter.sortBy !== 'checkInDate' ? filter.sortBy : undefined);
      set('sortDirection', filter.sortDirection && filter.sortDirection !== 'asc' ? filter.sortDirection : undefined);
      set('apartmentId', filter.apartmentId);
      set('checkInAfter', filter.checkInAfter);
      set('checkOutBefore', filter.checkOutBefore);
      set('guestName', filter.guestName);
      set('guestPhone', filter.guestPhone);
      set('showPast', showPast ? 'true' : undefined);
      return p;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, showPast]);

  const location = useLocation();
  const latestReservationsRequest = useRef(0);
  const showPastMounted = useRef(false);

  // Check if apartmentId was passed from navigation
  useEffect(() => {
    const state = location.state as { apartmentId?: number } | null;
    if (state?.apartmentId) {
      setFilter(f => ({
        ...f,
        apartmentId: state.apartmentId,
        page: 0,
      }));
    }
  }, [location]);

  const fetchReservations = useCallback(async (activeFilter: ReservationFilter) => {
    const requestId = ++latestReservationsRequest.current;
    setLoading(true);
    setError(null);
    try {
      const response = await searchReservations(activeFilter, getToken());
      // Ignore stale responses (e.g., initial unfiltered request finishing after filtered one).
      if (requestId !== latestReservationsRequest.current) return;
      setData(response);
    } catch (err: any) {
      if (requestId !== latestReservationsRequest.current) return;
      setError(err.response?.data?.message || err.message || 'Failed to load reservations');
    } finally {
      if (requestId === latestReservationsRequest.current) {
        setLoading(false);
      }
    }
  }, []);

  const fetchApartments = useCallback(async () => {
    try {
      const data = await getApartments(getToken());
      setApartments(data);
    } catch (err: any) {
      console.error('Failed to load apartments:', err);
    }
  }, []);

  useEffect(() => {
    fetchApartments();
  }, [fetchApartments]);

  useEffect(() => {
    fetchReservations(filter);
  }, [filter, fetchReservations]);

  useEffect(() => {
    localStorage.setItem(SHOW_PAST_STORAGE_KEY, showPast ? 'true' : 'false');
    // Skip the very first run: initial state already reflects showPast (from the URL or
    // localStorage), and resetting `page` here would stomp a page number restored from the URL.
    if (!showPastMounted.current) { showPastMounted.current = true; return; }
    setFilter(f => ({ ...f, page: 0, checkOutAfter: showPast ? undefined : todayIso() }));
  }, [showPast]);

  useEffect(() => {
    localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(pageSize));
  }, [pageSize]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilter(f => ({
      ...f,
      [name]: value === '' ? undefined : (name === 'apartmentId' || name === 'hostId' ? parseInt(value) : value),
      page: 0, // Reset to first page on filter change
    }));
  };

  const handleSearch = () => {
    fetchReservations(filter);
  };

  const handleClearFilters = () => {
    setSortField('checkInDate');
    setSortDirection('asc');
    setFilter({ page: 0, size: pageSize, sortBy: 'checkInDate', sortDirection: 'asc', checkOutAfter: showPast ? undefined : todayIso() });
  };


  const getPublicFormAbsoluteUrl = (reservation: Reservation) => {
    if (reservation.publicCheckinFormUrl) {
      return `${window.location.origin}${reservation.publicCheckinFormUrl}`;
    }
    if (reservation.publicCheckinFormToken) {
      return `${window.location.origin}/check-in/form/${reservation.publicCheckinFormToken}`;
    }
    return null;
  };

  const handleOpenGuestForm = async (reservation: Reservation) => {
    if (!reservation.id) return;

    let current = reservation;
    let formUrl = getPublicFormAbsoluteUrl(current);

    if (!formUrl) {
      setError(null);
      setSuccess(null);
      try {
        const res = await axios.post(`${API_BASE}/reservations/${reservation.id}/generate-public-checkin-form`, {}, {
          headers: { Authorization: `Bearer ${getToken()}` },
          withCredentials: true,
        });
        const updated = res.data as Reservation;
        current = { ...reservation, ...updated };
        setData((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            content: prev.content.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)),
          };
        });
        formUrl = getPublicFormAbsoluteUrl(current);
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'Unable to generate guest form URL');
        return;
      }
    }

    if (!formUrl) {
      setError('Unable to open guest form URL for this reservation.');
      return;
    }

    window.open(formUrl, '_blank', 'noopener,noreferrer');
  };

  const handlePageChange = (newPage: number) => {
    setFilter(f => ({ ...f, page: newPage }));
  };

  const formatDateInput = (date: Date) => date.toISOString().split('T')[0];

  const handleAddReservation = () => {
    const today = new Date();
    const tomorrow = new Date(today.getTime() + 86400000);
    setCreateForm({
      apartmentId: apartments.length > 0 ? apartments[0].id! : 0,
      guestName: '',
      checkInDate: formatDateInput(today),
      checkOutDate: formatDateInput(tomorrow),
      numberOfGuests: 1,
      notes: '',
    });
    setShowCreateForm(true);
    setError(null);
    setSuccess(null);
  };

  const handleEdit = (reservation: Reservation) => {
    // Carry the current filters/sort/page along in `back` so returning from the detail page
    // restores this exact view instead of resetting to an unfiltered list.
    if (reservation.id) navigate(`/reservations/${reservation.id}?back=${encodeURIComponent('/reservations' + location.search)}`);
  };

  const handleSubmitAllToUbyport = async (reservation: Reservation) => {
    if (!reservation.id) return;
    if (!window.confirm(t.confirmSubmitUbyport(reservation.guestName))) return;
    setSubmittingUbyportId(reservation.id);
    setError(null);
    try {
      const updated = await submitAllToUbyport(reservation.id, getToken());
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          content: prev.content.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)),
        };
      });
      if (updated.ubyportStatus === 'SENT') {
        setSuccess(t.ubyportSentSuccess);
      } else {
        setError(updated.ubyportError || 'Submission failed.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to submit to Ubyport.');
    } finally {
      setSubmittingUbyportId(null);
    }
  };

  const handleCreateFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setCreateForm((prev) => {
      const updated = {
        ...prev,
        [name]: name === 'apartmentId' || name === 'numberOfGuests' ? parseInt(value, 10) : value,
      };
      if (name === 'checkInDate' && value && updated.checkOutDate && updated.checkOutDate <= value) {
        updated.checkOutDate = dateAddDays(value, 1);
      }
      if (name === 'checkOutDate' && value && updated.checkInDate && updated.checkInDate >= value) {
        updated.checkInDate = dateAddDays(value, -1);
      }
      return updated;
    });
  };

  const handleCreateReservation = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await createReservation(createForm, getToken());
      setSuccess(t.reservationCreated);
      setShowCreateForm(false);
      await fetchReservations(filter);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to create reservation');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getUbyportBadge = (status?: string) => {
    switch (status) {
      case 'SENT':
        return { bg: '#c6f6d5', color: '#22543d', label: t.statusSent };
      case 'SENDING':
        return { bg: '#feebc8', color: '#7b341e', label: t.statusSending };
      case 'SEND_FAILED':
        return { bg: '#fed7d7', color: '#742a2a', label: t.statusFailed };
      case 'SEND_UNAVAILABLE':
        return { bg: '#feebc8', color: '#7b341e', label: t.statusUnavailable };
      case 'PARTIAL':
        return { bg: '#fed7d7', color: '#742a2a', label: t.statusPartial };
      case 'NOT_SENT':
      default:
        return { bg: '#e2e8f0', color: '#2d3748', label: t.statusNotSent };
    }
  };

  const getGuestbookBadge = (status?: string) => {
    switch (status) {
      case 'COMPLETED':
        return { bg: '#c6f6d5', color: '#22543d', label: t.statusCompleted };
      case 'INCOMPLETE':
        return { bg: '#feebc8', color: '#7b341e', label: t.statusIncomplete };
      case 'DEPRECATED':
        return { bg: '#e2e8f0', color: '#4a5568', label: t.statusDeprecated };
      case 'NOT_FILLED':
      default:
        return { bg: '#fed7d7', color: '#742a2a', label: t.statusNotFilled };
    }
  };

  const getCityTaxBadge = (status?: string) => {
    return status === 'PAID'
      ? { bg: '#c6f6d5', color: '#22543d', label: t.cityTaxPaidLabel }
      : { bg: '#fed7d7', color: '#742a2a', label: t.cityTaxUnpaidLabel };
  };

  const toggleSort = (field: ReservationSortField) => {
    const nextDirection: ReservationSortDirection =
      sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(nextDirection);
    setFilter((prev) => ({
      ...prev,
      page: 0,
      sortBy: field,
      sortDirection: nextDirection,
    }));
  };

  const getSortIndicator = (field: ReservationSortField) => {
    if (sortField !== field) return ' ↕';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  return (
    <div className="page-container">
      <h1 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: 20 }}>{t.pageTitle}</h1>

      {/* Filter bar — nav-panel card: quick filters always visible, rest behind "Show filters" */}
      <div style={{ marginBottom: 20, padding: 16, background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', gap: 8, flexWrap: isMobile ? 'nowrap' : 'wrap' }}>
          <button
            type="button"
            onClick={() => setFilterOpen((v) => !v)}
            aria-expanded={filterOpen}
            aria-controls="reservation-filter-section"
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: isMobile ? 'center' : 'flex-start', gap: 6,
              padding: '8px 12px', background: '#edf2f7', color: '#2d3748', border: '1px solid #e2e8f0', borderRadius: 6,
              fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', whiteSpace: 'nowrap',
              ...(isMobile ? { width: '100%' } : {}),
            }}
          >
            <FilterIcon size={16} color="#2d3748" /> {filterOpen ? tc.hideFilters : tc.showFilters}
          </button>
          <input
            type="text"
            name="guestName"
            value={filter.guestName || ''}
            onChange={handleFilterChange}
            placeholder={t.searchByName}
            style={{
              padding: '8px 12px', border: '1px solid #cbd5e0', borderRadius: 6, boxSizing: 'border-box',
              ...(isMobile ? { width: '100%' } : { flex: 1, minWidth: 160 }),
            }}
          />
          {/* Apartment select + Search button pair up on one row even when everything else stacks */}
          <div style={{ display: 'flex', gap: 8, ...(isMobile ? { width: '100%' } : {}) }}>
            <select
              name="apartmentId"
              value={filter.apartmentId || ''}
              onChange={handleFilterChange}
              style={{
                padding: '8px 12px', border: '1px solid #cbd5e0', borderRadius: 6, background: '#fff', color: '#2d3748', fontWeight: 600,
                ...(isMobile ? { flex: 1, minWidth: 0 } : {}),
              }}
            >
              <option value="">{t.filterAllApartments}</option>
              {apartments.map(apt => (
                <option key={apt.id} value={apt.id}>{apt.name}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleSearch}
              style={{
                padding: '8px 16px', background: colors.brand, color: '#fff', border: 'none', borderRadius: 6,
                fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
                ...(isMobile ? { flexShrink: 0 } : {}),
              }}
            >
              {tc.search}
            </button>
          </div>
        </div>

        {filterOpen && (
          <div id="reservation-filter-section" style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #e2e8f0' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: '0.9rem' }}>{t.filterCheckInAfter}</label>
                <input type="date" name="checkInAfter" value={filter.checkInAfter || ''} onChange={handleFilterChange} style={{ width: '100%', boxSizing: 'border-box', padding: '8px', borderRadius: 4, border: '1px solid #cbd5e0' }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: '0.9rem' }}>{t.filterCheckOutBefore}</label>
                <input type="date" name="checkOutBefore" value={filter.checkOutBefore || ''} onChange={handleFilterChange} style={{ width: '100%', boxSizing: 'border-box', padding: '8px', borderRadius: 4, border: '1px solid #cbd5e0' }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: '0.9rem' }}>{t.filterGuestPhone}</label>
                <input type="text" name="guestPhone" value={filter.guestPhone || ''} onChange={handleFilterChange} placeholder={t.searchInNotes} style={{ width: '100%', boxSizing: 'border-box', padding: '8px', borderRadius: 4, border: '1px solid #cbd5e0' }} />
              </div>
            </div>
            <div style={{ marginTop: 16 }}>
              <button type="button" onClick={handleClearFilters} className="btn btn-sm btn-muted">{tc.clearFilters}</button>
            </div>
          </div>
        )}
      </div>

      {/* Action bar — Add left, matches Home's structure */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
        <button
          type="button"
          onClick={handleAddReservation}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            background: '#38a169', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 20px',
            fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem',
            ...(isMobile ? { width: '100%' } : {}),
          }}
        >
          <PlusIcon size={16} color="#fff" /> {t.addReservation}
        </button>
      </div>

      {success && <div style={{ color: 'green', marginBottom: 12, padding: 12, background: '#f0fff4', borderRadius: 6 }}>{success}</div>}
      {error && <div style={{ color: 'red', marginBottom: 12, padding: 12, background: '#fff5f5', borderRadius: 6 }}>{error}</div>}

      {showCreateForm && (
        <div className="form-card" style={{ marginBottom: 16 }}>
          <h3 style={{ marginTop: 0 }}>{t.newReservationTitle}</h3>
          <form onSubmit={handleCreateReservation}>
            <div className="form-grid">
              <div className="form-field">
                <label className="form-label">{t.formApartment}</label>
                <select className="form-control" name="apartmentId" value={createForm.apartmentId} onChange={handleCreateFormChange} required>
                  <option value="">{t.selectApartment}</option>
                  {apartments.map((apt) => (
                    <option key={apt.id} value={apt.id}>{apt.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label className="form-label">{t.formGuestName}</label>
                <input className="form-control" name="guestName" value={createForm.guestName} onChange={handleCreateFormChange} required />
              </div>
              <div className="form-field">
                <label className="form-label">{t.formCheckInDate}</label>
                <input className="form-control" type="date" name="checkInDate" value={createForm.checkInDate} onChange={handleCreateFormChange} required />
              </div>
              <div className="form-field">
                <label className="form-label">{t.formCheckOutDate}</label>
                <input className="form-control" type="date" name="checkOutDate" value={createForm.checkOutDate} onChange={handleCreateFormChange} required />
              </div>
              <div className="form-field">
                <label className="form-label">{t.formNumberOfGuests}</label>
                <input className="form-control" type="number" min={1} name="numberOfGuests" value={createForm.numberOfGuests ?? ''} onChange={handleCreateFormChange} required />
              </div>
              <div className="form-field" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">{t.formNotes}</label>
                <textarea className="form-control" name="notes" value={createForm.notes || ''} onChange={handleCreateFormChange} rows={3} />
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-sm btn-primary" disabled={isSubmitting}>{isSubmitting ? '…' : tc.save}</button>
              <button type="button" onClick={() => setShowCreateForm(false)} className="btn btn-sm btn-muted">{tc.cancel}</button>
            </div>
          </form>
        </div>
      )}

      {/* Summary row - outside card, matches Apartments/Hosts layout */}
      <div
        style={{
          marginBottom: 12,
          fontSize: '0.9rem',
          color: '#718096',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <span>
          {!loading && data && data.content.length > 0
            ? <>{t.showing(data.content.length, data.totalElements, data.page + 1, data.totalPages)}</>
            : <>&nbsp;</>}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {/* Segmented toggle — matches Home's Ongoing/Incoming control */}
          <div style={{ display: 'flex', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => setShowPast(true)}
              aria-pressed={showPast}
              style={{
                padding: '8px 14px', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
                border: 'none', borderRight: '1px solid #e2e8f0',
                background: showPast ? '#2d3748' : '#fff',
                color: showPast ? '#fff' : '#718096',
              }}
            >
              {t.includePast}
            </button>
            <button
              type="button"
              onClick={() => setShowPast(false)}
              aria-pressed={!showPast}
              style={{
                padding: '8px 14px', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
                border: 'none',
                background: !showPast ? '#2d3748' : '#fff',
                color: !showPast ? '#fff' : '#718096',
              }}
            >
              {t.upcoming}
            </button>
          </div>
          {/* Page size */}
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span>{t.itemsPerPage}</span>
            <select
              value={pageSize}
              onChange={(e) => {
                const next = parseInt(e.target.value, 10);
                setPageSize(next);
                setFilter((f) => ({ ...f, page: 0, size: next }));
              }}
              style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e0', background: '#fff' }}
            >
              {RESERVATION_PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#718096' }}>{t.loadingReservations}</div>
      ) : !data || data.content.length === 0 ? (
        apartments.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 24px', background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <div style={{ fontSize: '3rem', marginBottom: 16 }}>🏠</div>
            <h2 style={{ margin: '0 0 12px', fontSize: '1.4rem', color: '#1a202c' }}>{t.emptyNoApartmentsTitle}</h2>
            <p style={{ color: '#718096', maxWidth: 460, margin: '0 auto 28px', lineHeight: 1.6 }}>{t.emptyNoApartmentsBody}</p>
            <Link
              to="/apartments"
              style={{ display: 'inline-flex', alignItems: 'center', background: '#38a169', color: '#fff', borderRadius: 8, padding: '12px 24px', fontWeight: 700, fontSize: '1rem', textDecoration: 'none' }}
            >
              {t.emptyNoApartmentsCta}
            </Link>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '48px 24px', background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: '1.6rem' }}>
              📅
            </div>
            <h2 style={{ margin: '0 0 8px', fontSize: '1.2rem', color: '#1a202c' }}>{t.noReservationsFoundTitle}</h2>
            <p style={{ color: '#718096', margin: '0 auto 8px', maxWidth: 420, lineHeight: 1.6 }}>{t.noReservations}</p>
            <div style={{ margin: '16px 0 24px', fontSize: '0.88rem', color: '#a0aec0' }}>
              {t.calendarSyncHint}{' '}
              <Link
                to={`/apartments/${apartments[0].id}?tab=calendar`}
                style={{ color: '#3182ce', fontWeight: 600 }}
              >
                {t.calendarSyncLink}
              </Link>
            </div>
            <button
              type="button"
              onClick={handleAddReservation}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#38a169', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 24px', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer' }}
            >
              <PlusIcon size={16} color="#fff" /> {t.addReservation}
            </button>
          </div>
        )
      ) : (
        <div
          className="reservation-results-container"
          style={{ background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: 20 }}
        >
          <>
            {isMobile ? (
              <div className="reservation-mobile-list" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {data.content.map((reservation) => {
                  const r = reservation as Reservation & { guestbookStatus?: string };
                  const uby = getUbyportBadge(r.ubyportStatus);
                  const guestbook = getGuestbookBadge(r.guestbookStatus);
                  const cityTax = r.cityTaxCollectionEnabled ? getCityTaxBadge(r.cityTaxStatus) : null;
                  return (
                    <ReservationItem
                      key={r.id}
                      reservation={r}
                      layout='compact'
                      apartmentText={r.apartmentName || `Apartment #${r.apartmentId}`}
                      guestbookStatus={guestbook}
                      ubyportStatus={uby}
                      cityTaxStatus={cityTax}
                      onOpenGuestForm={handleOpenGuestForm}
                      onEdit={handleEdit}
                    />
                  );
                })}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                      <th style={{ padding: '12px 8px 12px 12px', textAlign: 'left', fontWeight: 600, color: '#718096', whiteSpace: 'nowrap', width: 1 }}>{t.tableNum}</th>
                      <th style={{ padding: 12, textAlign: 'left', fontWeight: 600 }}>{t.tableGuest}</th>
                      <th style={{ padding: 12, textAlign: 'left', fontWeight: 600 }}>
                        <button
                          type="button"
                          onClick={() => toggleSort('apartment')}
                          style={{ background: 'transparent', border: 'none', padding: 0, fontWeight: 600, cursor: 'pointer', color: '#2d3748' }}
                        >
                          {t.tableApartment}{getSortIndicator('apartment')}
                        </button>
                      </th>
                      <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        <button
                          type="button"
                          onClick={() => toggleSort('checkInDate')}
                          style={{ background: 'transparent', border: 'none', padding: 0, fontWeight: 600, cursor: 'pointer', color: '#2d3748' }}
                        >
                          {t.tableCheckin.split(' → ')[0]}{getSortIndicator('checkInDate')}
                        </button>
                        {' → '}
                        <button
                          type="button"
                          onClick={() => toggleSort('checkOutDate')}
                          style={{ background: 'transparent', border: 'none', padding: 0, fontWeight: 600, cursor: 'pointer', color: '#2d3748' }}
                        >
                          {t.tableCheckin.split(' → ')[1] || 'Check-out'}{getSortIndicator('checkOutDate')}
                        </button>
                      </th>
                      <th style={{ padding: 12, textAlign: 'left', fontWeight: 600 }}>{t.tableGuests}</th>
                      <th style={{ padding: 12, textAlign: 'left', fontWeight: 600 }}>{t.tableGuestbook}</th>
                      <th style={{ padding: 12, textAlign: 'left', fontWeight: 600 }}>{t.tableUbyport}</th>
                      <th style={{ padding: 12, textAlign: 'left', fontWeight: 600 }}>{t.tableActions}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.content.map((reservation) => {
                      const r = reservation as Reservation & { guestbookStatus?: string };
                      const uby = getUbyportBadge(r.ubyportStatus);
                      const guestbook = getGuestbookBadge(r.guestbookStatus);
                      const isOverbooked = r.status === 'OVERBOOKED';
                      const isInactive = isOverbooked || r.status === 'CANCELLED' || r.guestbookStatus === 'DEPRECATED';
                      const trBg = isOverbooked ? '#fff5f5' : isInactive ? '#f7f8fa' : undefined;
                      const hasManageLink = !!(r.id && r.publicCheckinFormToken);
                      // Day and month, plus the year only when the stay crosses into the next one.
                      const stay = formatStayRange(r.checkInDate, r.checkOutDate);
                      return (
                        <tr key={reservation.id} style={{ borderBottom: `1px solid ${isOverbooked ? '#fed7d7' : '#e2e8f0'}`, background: trBg, opacity: isInactive && !isOverbooked ? 0.8 : 1 }}>
                          <td style={{ padding: '12px 8px 12px 12px', color: '#a0aec0', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                            #{reservation.id}
                          </td>
                          <td style={{ padding: 12 }}>
                            <div style={{ fontWeight: 600, color: isInactive ? '#6b7280' : undefined }}>{reservation.guestName}</div>
                            {isOverbooked && (
                              <div style={{ fontSize: '0.8rem', color: '#c53030', marginTop: 2 }}>{t.inactiveConflict}</div>
                            )}
                            {!isOverbooked && r.status === 'CANCELLED' && (
                              <span style={{ display: 'inline-block', marginTop: 2, padding: '1px 7px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 700, background: '#fed7d7', color: '#9b2c2c' }}>
                                {t.cancelledLabel}
                              </span>
                            )}
                            {!isOverbooked && r.guestbookStatus === 'DEPRECATED' && r.status !== 'CANCELLED' && (
                              <span style={{ display: 'inline-block', marginTop: 2, padding: '1px 7px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 700, background: '#e2e8f0', color: '#4a5568' }}>
                                {t.deprecatedLabel}
                              </span>
                            )}
                            {reservation.notes && (
                              <div style={{ fontSize: '0.85rem', color: '#718096', marginTop: 2 }}>{reservation.notes.substring(0, 50)}{reservation.notes.length > 50 ? '...' : ''}</div>
                            )}
                          </td>
                          <td style={{ padding: 12 }}>
                            {reservation.apartmentId ? (
                              <Link
                                to={`/apartments/${reservation.apartmentId}`}
                                style={{ color: '#3182ce', fontWeight: 600, textDecoration: 'none' }}
                                title="Open apartment detail"
                              >
                                {reservation.apartmentName || `Apartment #${reservation.apartmentId}`}
                              </Link>
                            ) : (
                              reservation.apartmentName || '—'
                            )}
                          </td>
                          {/* Vertical padding trimmed so the second line costs about four pixels of
                              row height rather than half a row. Only this cell changes. */}
                          <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                            <div style={{ lineHeight: 1.3 }}>{stay.from} → {stay.to}</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                              <StayHours checkin={r.checkinHours} checkout={r.checkoutHours} />
                              <LuggageStatusIcon status={r.luggageStatus} />
                            </div>
                          </td>
                          <td style={{ padding: 12 }}>{reservation.numberOfGuests ?? '?'}</td>
                          <td style={{ padding: 12 }}>
                            <span style={{
                              display: 'inline-block',
                              padding: '4px 8px',
                              borderRadius: 4,
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              background: guestbook.bg,
                              color: guestbook.color
                            }}>
                              {guestbook.label}
                            </span>
                          </td>
                          <td style={{ padding: 12 }}>
                            <span style={{
                              display: 'inline-block',
                              padding: '4px 8px',
                              borderRadius: 4,
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              background: uby.bg,
                              color: uby.color
                            }}>
                              {uby.label}
                            </span>
                            {(() => {
                              const today = new Date().toISOString().slice(0, 10);
                              const oneYearAgo = new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
                              const validDate = r.checkInDate <= today && r.checkInDate >= oneYearAgo;
                              return validDate && r.guestbookStatus === 'COMPLETED' && (r.ubyportStatus === 'NOT_SENT' || r.ubyportStatus === 'SEND_FAILED' || r.ubyportStatus === 'SEND_UNAVAILABLE' || r.ubyportStatus === 'PARTIAL' || !r.ubyportStatus);
                            })() && (
                              <div style={{ marginTop: 6 }}>
                                <button
                                  onClick={() => handleSubmitAllToUbyport(r)}
                                  disabled={submittingUbyportId === r.id}
                                  style={{ padding: '4px 10px', background: submittingUbyportId === r.id ? '#bee3f8' : '#2b6cb0', color: '#fff', border: 'none', borderRadius: 4, cursor: submittingUbyportId === r.id ? 'not-allowed' : 'pointer', fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap' }}
                                >
                                  {submittingUbyportId === r.id ? t.submitting : t.submitAll}
                                </button>
                              </div>
                            )}
                          </td>
                          <td style={{ padding: 12 }}>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              <button onClick={() => handleEdit(reservation)} title="Edit" style={{ width: ICON_BTN_SIZE, height: ICON_BTN_SIZE, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: colors.brand, color: '#fff', border: 'none', borderRadius: ACTION_BTN_RADIUS, cursor: 'pointer' }}>
                                <EditIcon size={14} />
                              </button>
                              <button onClick={() => handleOpenGuestForm(reservation)} disabled={isOverbooked} style={{ height: ACTION_BTN_H, padding: '0 12px', background: isOverbooked ? '#a0aec0' : colors.brand, color: '#fff', border: 'none', borderRadius: ACTION_BTN_RADIUS, cursor: isOverbooked ? 'not-allowed' : 'pointer', fontSize: '0.85rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                {isOverbooked ? t.inactiveLabel : t.guestForm}
                              </button>
                              {hasManageLink && (
                                <Link
                                  to={`/reservations/${r.id}/checkin-form`}
                                  style={{ display: 'inline-flex', alignItems: 'center', height: ACTION_BTN_H, padding: '0 12px', background: '#edf2f7', color: '#2d3748', borderRadius: ACTION_BTN_RADIUS, fontSize: '0.85rem', fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }}
                                >
                                  {t.manage}
                                </Link>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            <Pagination
              currentPage={data.page}
              totalPages={data.totalPages}
              onPageChange={handlePageChange}
            />
          </>
        </div>
      )}
    </div>
  );
};

export default Reservations;

